/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
import { useMemo } from "react";
import { useArticleById } from "../hooks/useArticleById";
import { useMediaById } from "../hooks/useMediaById";
import { useParams, useSearchParams } from "react-router";
import { useTranslation } from "@coworker/locales";
import { MediaPlacement } from "../Molecules/MediaPlacement";
import { useTasksByArticle } from "../hooks/useTasksByArticle";
import { useMediasAndArticleCount } from "../hooks/useMediasAndArticleCount";
import {
  getPrimeTasks,
  getSortedMediasArticleCount,
} from "./ArticleView.helper";
import { ArticleViewSkeleton } from "../Skeletons/ArticleViewSkeleton";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { ArticleListItemMobile } from "../styles/styles";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import LinkbarLoading from "@coworker/apprestructured/src/shared/components/Linkbar/LinkbarLoading";
import { AboutItemSection } from "@coworker/app/src/components/RoomSettings/Articles/AboutItemSection";
import { ItemListViewItem } from "@coworker/apprestructured/src/media-maintenance/components/ItemListViewItem/ItemListViewItem";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { useTasksByMedia } from "@coworker/app/src/components/RoomSettings/hooks/useTasksByMedia";
import { useEDSData } from "@coworker/app/src/components/RoomSettings/hooks/useEDSData";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useSalesStopData } from "@coworker/app/src/components/RoomSettings/hooks/useSalesStopData";
import {
  isArticleInCombination,
  isCombinationArticleValid,
} from "@coworker/app/src/components/RoomSettings/Helpers/articleHelpers";
import LocalCombinationSubArticlePanel from "@coworker/app/src/components/RoomSettings/Molecules/LocalCombinationSubArticlePanel";
import { useCombinationArticleData } from "@coworker/app/src/components/RoomSettings/hooks/useCombinationArticle";
import { MediaItemType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemType";
import { ArticleTasksSection } from "../Organisms/ArticleTasksSection";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";

export function ArticleView() {
  const { articleId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const mediaItemType =
    (searchParams.get("media-item-type") as MediaItemType) || undefined;

  const { data: article, isLoading: articleLoading } =
    useArticleById(articleId);
  const { data: mediaResponse, isLoading: mediaLoading } = useMediaById(
    article?.mediaId
  );
  const { tasks } = useTasksByArticle(
    mediaResponse?.media.storeId,
    article?.productArticleId
  );
  const { data: mediasArticleCount, isLoading: racLoading } =
    useMediasAndArticleCount(
      article?.productArticleId,
      mediaResponse?.media.storeId
    );
  const countryId = useCountryId();
  const { taskLinks } = useTasksByMedia(article?.mediaId);
  const { data: edsData } = useEDSData(
    countryId,
    article?.productArticleId || ""
  );

  const isMediaSubItem = mediaItemType === MediaItemType.LC_SUBITEM;
  // If media is a subitem, we need to fetch the combination article to show the parent article info
  const { data: combinationArticle } = useCombinationArticleData(
    article?.id,
    isMediaSubItem
  );
  const storeId = useStoreId();
  const { data: salesStopData } = useSalesStopData(
    storeId,
    article?.productArticleId ?? ""
  );

  const sortedMediasArticleCount = getSortedMediasArticleCount(
    mediasArticleCount || [],
    mediaResponse?.media.name || ""
  );
  const primeTasks = getPrimeTasks(mediasArticleCount || [], tasks);

  const { data: itemInfo, isLoading } = useItemInfo(
    article?.productArticleId ?? ""
  );
  const linkbar = useMemo(() => {
    if (
      !article?.itemName ||
      !mediaResponse?.media.name ||
      !mediaResponse?.media.id
    ) {
      return <LinkbarLoading />;
    }

    let linkBarItems;
    if (!article?.groupId || !article?.groupName) {
      linkBarItems = [
        {
          label: t("mediaMaintenanceString"),
          to: "/mediamaintenance",
        },
        {
          label: mediaResponse.media.name,
          to: `/mediamaintenance/tabs/${mediaResponse.media.id}?tab=article-list-tab`,
        },
      ];
    } else {
      linkBarItems = [
        {
          label: t("mediaMaintenanceString"),
          to: "/mediamaintenance",
        },
        {
          label: mediaResponse.media.name,
          to: `/mediamaintenance/tabs/${mediaResponse.media.id}?tab=article-list-tab`,
        },
      ];
    }

    if (mediaResponse.parent?.name) {
      linkBarItems.splice(1, 0, {
        label: mediaResponse.parent.name,
        to: `/mediamaintenance/tabs/${mediaResponse.parent.id}?tab=article-list-tab`,
      });
    }

    return <Linkbar currentLabel={article?.itemName} items={linkBarItems} />;
  }, [
    article?.groupId,
    article?.groupName,
    article?.itemName,
    mediaResponse?.media.id,
    mediaResponse?.media.name,
    t,
    mediaResponse?.parent?.name,
    mediaResponse?.parent?.id,
  ]);

  if (articleLoading || mediaLoading || racLoading)
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <ArticleViewSkeleton />
      </FullScreenPopup>
    );

  return (
    <>
      {article && mediaResponse && (
        <FullScreenPopup noPadding linkbar={linkbar}>
          {(isArticleInCombination(article) || isMediaSubItem) && (
            <LocalCombinationSubArticlePanel
              parentArticle={
                isCombinationArticleValid(combinationArticle)
                  ? combinationArticle
                  : undefined
              }
            />
          )}
          <ArticleListItemMobile>
            <ItemListViewItem
              mediaItemType={mediaItemType}
              article={article}
              itemCardRenderType={ItemCardRenderType.link}
              media={mediaResponse?.media}
              taskLinks={taskLinks}
              edsData={
                edsData?.find(
                  (eds: { productArticleId: string }) =>
                    eds.productArticleId === article.productArticleId
                ) ?? null
              }
              salesStopData={
                salesStopData?.find(
                  (salesStop) =>
                    salesStop.productArticleId === article.productArticleId
                ) ?? null
              }
              itemInfo={itemInfo ?? null}
              itemInfoLoading={isLoading}
            />
          </ArticleListItemMobile>
          <FixaListView id="article-view-list" size={"small"}>
            <MediaPlacement
              mediasArticleCount={sortedMediasArticleCount || []}
              currentArticleId={article.id}
            />

            <AboutItemSection article={article} />
          </FixaListView>

          {(!isArticleInCombination(article) || !isMediaSubItem) && (
            <ArticleTasksSection
              mediasArticleCount={sortedMediasArticleCount || []}
              tasks={primeTasks}
              currentArticleId={article.id}
            />
          )}
        </FullScreenPopup>
      )}
    </>
  );
}
